/*
  Ballr — cart recommendations (carousel + quick-add popup).

  Runs again every time the cart drawer re-renders: Dawn replaces the drawer's innerHTML,
  and a <script> injected that way never executes, so everything here is driven by a
  MutationObserver on <cart-drawer> instead.

  Three jobs:
  1. Carousel arrows.
  2. Re-point QuickAddModal.modalContent. The class caches that child in its constructor,
     but markup injected as innerHTML upgrades the element before its children exist, so
     the cached value is undefined and show() throws.
  3. Keep the drawer popups on <body>. Inside the drawer they would be trapped by the
     drawer's `transform` (which becomes the containing block for fixed children) and
     clipped by its `overflow: hidden`; Dawn moves them there itself, this prunes the
     copies left behind by earlier renders.
*/
(function () {
  var REC = '[data-ballr-rec]';
  var MODAL = 'quick-add-modal[data-ballr-rec-modal]';

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function hydrateModals() {
    each(document.querySelectorAll(MODAL), function (modal) {
      if (!modal.modalContent) {
        modal.modalContent = modal.querySelector('[id^="QuickAddInfo-"]');
      }
    });
  }

  function relocateModals() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // Dawn's ModalDialog moves each popup to <body> as soon as it connects. Move any it left
    // behind (older global.js threw before getting that far).
    each(drawer.querySelectorAll('quick-add-modal[data-ballr-rec-modal="drawer"]'), function (modal) {
      document.body.appendChild(modal);
    });

    // Every drawer re-render brings a fresh set, so keep only the newest copy of each id and
    // drop the leftovers from earlier renders - but never one that is open.
    var seen = {};
    var popups = document.querySelectorAll('body > quick-add-modal[data-ballr-rec-modal="drawer"]');
    for (var i = popups.length - 1; i >= 0; i--) {
      var popup = popups[i];
      if (popup.hasAttribute('open')) {
        seen[popup.id] = true;
        continue;
      }
      if (seen[popup.id]) popup.remove();
      else seen[popup.id] = true;
    }
  }

  function initCarousel(rec) {
    var list = rec.querySelector('.ballr-rec__list');
    var prev = rec.querySelector('[data-ballr-rec-prev]');
    var next = rec.querySelector('[data-ballr-rec-next]');
    if (!list || !prev || !next) return;

    // Only flag it once everything it needs is actually in the DOM.
    if (rec.dataset.ballrRecReady === 'true') return;
    rec.dataset.ballrRecReady = 'true';

    function step() {
      var item = list.querySelector('.ballr-rec__item');
      if (!item) return list.clientWidth;
      var styles = getComputedStyle(list);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return Math.round(item.getBoundingClientRect().width + gap);
    }

    function update() {
      var max = list.scrollWidth - list.clientWidth;
      rec.classList.toggle('ballr-rec--has-nav', max > 1);
      prev.disabled = list.scrollLeft <= 1;
      next.disabled = list.scrollLeft >= max - 1;
    }

    /*
      Animate scrollLeft by hand. Native smooth scrolling (CSS scroll-behavior and
      scrollBy({behavior:'smooth'})) silently does nothing inside the cart drawer, so the
      arrows looked dead — a plain scrollLeft assignment is the only thing that moves it.
      Snapping is switched off for the duration so it cannot fight the tween, and a timer
      backstops requestAnimationFrame, which never fires in a background tab.
    */
    function scrollBySteps(direction) {
      var max = list.scrollWidth - list.clientWidth;
      var from = list.scrollLeft;
      var to = Math.max(0, Math.min(max, from + step() * direction));
      if (Math.abs(to - from) < 1) return;

      var duration = 300;
      var startedAt = null;
      var painted = false;
      var done = false;

      list.style.scrollSnapType = 'none';

      function finish() {
        if (done) return;
        done = true;
        list.scrollLeft = to;
        list.style.scrollSnapType = '';
        update();
      }

      function frame(now) {
        if (done) return;
        if (startedAt === null) startedAt = now;
        painted = true;
        var progress = Math.min(1, (now - startedAt) / duration);
        var eased = 0.5 - Math.cos(Math.PI * progress) / 2;
        list.scrollLeft = from + (to - from) * eased;
        if (progress < 1) requestAnimationFrame(frame);
        else finish();
      }

      requestAnimationFrame(frame);

      // No frames (hidden tab, reduced motion): jump straight to the target.
      setTimeout(function () {
        if (!painted) finish();
      }, 120);
      setTimeout(finish, duration + 250);
    }

    prev.addEventListener('click', function () {
      scrollBySteps(-1);
    });
    next.addEventListener('click', function () {
      scrollBySteps(1);
    });
    list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    update();
    // Images settle after load and change the scrollable width.
    setTimeout(update, 400);
  }

  function init() {
    each(document.querySelectorAll(REC), initCarousel);
    relocateModals();
    hydrateModals();
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // Debounced with a timer, not requestAnimationFrame: rAF does not fire in a
    // background tab, and the re-render can happen while the tab is hidden.
    var queued = null;
    new MutationObserver(function () {
      clearTimeout(queued);
      queued = setTimeout(init, 60);
    }).observe(drawer, { childList: true, subtree: true });
  }

  function start() {
    init();
    watchDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
